import React, { useEffect, useRef, useState } from 'react';

import jookableLogo from '../images/Jook_Web_White.png';

import * as JKYModel from '../custom-types';

import { ACCEPTED_LOGO_TYPES, loadLocalVenueLogo } from '../utils/VenueLogo';
import { useVenueLogoEditor } from '../utils/custom-hooks/VenueLogoEditor';

import './IdleScreen.scss';

interface IdleScreenProps {
  player?: JKYModel.Player;
  playerIdentifier: string;
  /** The signed-in account owns this player, so it may change the logo from here. */
  canEdit: boolean;
}

// Shown in the video area while nothing is queued. It shows the venue's logo, stored on
// the server, and the Jookable logo otherwise. A logo saved in this browser before logos
// moved to the server is still shown here until the owner uploads one; see utils/VenueLogo.
const IdleScreen = ({ player, playerIdentifier, canEdit }: IdleScreenProps) => {
  const serverLogo = player && player.logoUrl;
  const [localLogo, setLocalLogo] = useState<Blob | null>(null);
  const [localLogoUrl, setLocalLogoUrl] = useState<string>();
  const fileInput = useRef<HTMLInputElement>(null);
  const editor = useVenueLogoEditor(player, () => setLocalLogo(null));

  useEffect(() => {
    let cancelled = false;
    if (!serverLogo) {
      loadLocalVenueLogo(playerIdentifier).then((stored) => {
        if (!cancelled) setLocalLogo(stored);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [playerIdentifier, serverLogo]);

  useEffect(() => {
    if (!localLogo) {
      setLocalLogoUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(localLogo);
    setLocalLogoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [localLogo]);

  const logoUrl = serverLogo || localLogoUrl;

  const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    // Clear the input so choosing the same file again still fires a change.
    e.target.value = '';
    if (file) editor.upload(file);
  };

  return (
    <div className="idle-screen">
      {logoUrl ? (
        <img className="idle-screen-logo" src={logoUrl} alt="Venue logo" />
      ) : (
        <img className="idle-screen-logo idle-screen-logo-default" src={jookableLogo} alt="Jookable logo" />
      )}
      <p className="idle-screen-caption">Add a song to start the music</p>

      {canEdit && (
        <div className={`idle-screen-controls${editor.message ? ' has-message' : ''}`}>
          <input ref={fileInput} type="file" accept={ACCEPTED_LOGO_TYPES.join(',')} onChange={onFileChosen} hidden />
          <button type="button" disabled={editor.busy} onClick={() => fileInput.current && fileInput.current.click()}>
            {logoUrl ? 'Change venue logo' : 'Upload venue logo'}
          </button>
          {logoUrl && (
            <button type="button" disabled={editor.busy} onClick={editor.remove}>
              Use Jookable logo
            </button>
          )}
          {editor.message && (
            <p className="idle-screen-message" role={editor.tone === 'bad' ? 'alert' : 'status'}>
              {editor.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default IdleScreen;
